// ─── Plan tier definitions ──────────────────────────────────────────
//
// Defines the feature limits for each subscription tier.
// Free users get a basic limited workspace.
// Paid tiers unlock larger canvases, cloud storage, co-op, and more.
//
// ENTERPRISE is special: limits are per-tenant, defined by contract.
// Enterprise users belong to a tenant and inherit its custom limits.

export type PlanTier = 'free' | 'advanced' | 'pro' | 'max' | 'enterprise';

export interface PlanLimits {
  /** Display name */
  name: string;
  /** Canvas size multiplier: 1 = 1920×1080, 2 = 2×(1920×1080), etc. -1 = unlimited */
  canvasMultiplier: number;
  /** Max canvas width in px. -1 = unlimited (dynamic) */
  canvasWidth: number;
  /** Max canvas height in px. -1 = unlimited (dynamic) */
  canvasHeight: number;
  /** Max workspaces the user can own */
  maxWorkspaces: number;
  /** Max workspaces saved to cloud DB */
  maxCloudStorage: number;
  /** Max nested group depth, per sheet */
  maxNestedLayers: number;
  /** Max top-level layers/groups, per sheet */
  maxTopLevelGroups: number;
  /** Max co-op (shared) workspaces */
  maxCoopWorkspaces: number;
  /** Max blocks per sheet (not per workspace — a workspace can hold
   *  up to `maxSheets` sheets, each with its own block budget) */
  maxBlocks: number;
  /** Max connectors per sheet */
  maxConnectors: number;
  /** Can export overview as image */
  canExportOverview: boolean;
  /** Can use AI assistant */
  canUseAI: boolean;
  /** Can simulate workflows */
  canSimulate: boolean;
  /** Can use advanced overview modes (detailed/tree/compact) */
  canUseAdvancedOverview: boolean;
  /** Can use minimap navigator */
  canUseMinimapNav: boolean;
  /** Can link workspaces into super-web */
  canLinkWorkspaces: boolean;
  /** Max sheets/tabs per workflow. -1 = unlimited. 1 = no tab bar shown. */
  maxSheets: number;
  /**
   * Max aktiva conversations i chat-historiken (Batch EEE, fråga 2).
   * -1 = unlimited. Archive:ade räknas inte. Soft-deleted räknas inte.
   */
  maxConversations: number;
  /**
   * Max artifacts per conversation (Batch EEE).
   * -1 = unlimited. Varje version räknas inte separat — bara artifact-heads.
   */
  maxArtifactsPerConversation: number;
  /**
   * Tillåtna AI-modeller (matchning mot model-slug).
   * Första modellen är default. Lovable-gateway-slugs.
   * readonly array för type-säkerhet.
   */
  allowedModels: readonly string[];
  /**
   * P-001: Kan user köra workflows som process (trigger-based
   * execution, run-history, scheduled runs, webhook-triggers, API-
   * invoke). Enterprise-only feature. Klient-gate är cosmetic —
   * server-side edge-fn (run-workflow, webhook-trigger, etc.) gör
   * sin egen tier-check mot profiles.plan_tier.
   */
  canRunProcesses: boolean;
  /**
   * Zoom-läge för canvas:
   *  - 'static' = CSS-transform-zoom (vanlig viewport-skalning)
   *  - 'dynamic' = skalar block-dimensionerna i koordinatrum
   *    (ports + connectors omberäknas mot nya bounds; mer block ryms
   *    samtidigt utan att zoom:a ut viewporten)
   * Mobile tvångar 'static' oavsett tier (dynamic är desktop-feature).
   */
  zoomMode: 'static' | 'dynamic';
  /** Min-zoom (0..1). Fit-to-screen klampas till denna nedre gräns. */
  zoomMin: number;
  /** Max-zoom (1.0 default). Fit-to-screen klampas till denna övre gräns. */
  zoomMax: number;
  /**
   * Atlas tier-gating (Phase 1 of the 100-step Atlas completion plan).
   * Atlas is a Pro/Max/Enterprise feature; Free + Advanced see an
   * upsell card on `/atlas`.
   */
  /** Can view atlas at all. Pro+. False = `/atlas` shows upsell. */
  atlasEnabled: boolean;
  /** Can scan public GitHub repos for atlas auto-discovery. Max+. */
  atlasGithubScanner: boolean;
  /** Can use AI hooks that take atlas context (useExplainAtlas, etc.). Max+. */
  atlasAI: boolean;
  /**
   * Can authenticate to private GitHub repos for atlas loading. Enterprise-only.
   * Without this, only public repos work via the scanner.
   */
  atlasPrivateRepos: boolean;
  /**
   * Max documents per atlas. -1 = unlimited. Loaded atlases exceeding
   * this surface a "too large for your plan" banner; doc-open is blocked.
   */
  atlasMaxDocs: number;
  /**
   * Max sheets per document inside an atlas. -1 = unlimited. Same gating
   * pattern as `atlasMaxDocs`.
   */
  atlasMaxSheetsPerDoc: number;
  /**
   * Mega-canvas view (`/atlas/canvas`) — single-canvas overview of an
   * entire atlas with all docs as cards. Max+. Phase 8 of completion plan.
   */
  atlasMegaCanvas: boolean;
  /**
   * "Pin atlas for offline" — forces all docs of an atlas into the
   * IndexedDB cache regardless of access pattern. Enterprise-only.
   * Phase 9 of completion plan.
   */
  atlasOfflinePinning: boolean;
  /**
   * Search Canvas — `/search` route with full-page virtual canvas of
   * matched entities, color-coded by source. Pro+.
   */
  searchCanvasEnabled: boolean;
  /**
   * Smart probing inside Search Canvas: find-similar, trace-path,
   * show-neighbors, save/playback probe trail. Max+.
   */
  searchProbingEnabled: boolean;
  /**
   * Atlas Code-Probe sub-system — fetch GitHub repos, run static analysis,
   * generate the 8 standardised English-language docs. Pro+ for the
   * structural/template flow; AI deep analysis additionally requires
   * `codeProbeAi` (Max+).
   */
  codeProbeEnabled: boolean;
  /** AI deep-analysis layer on top of Code-Probe. Max+/Enterprise. */
  codeProbeAi: boolean;
  /** Daily quota for Code-Probe runs per user. */
  codeProbeMaxPerDay: number;
  /**
   * Atlas-aware minimap: doc-icon distinction + double-click drill-down.
   * Pro+. See documents/v1/MINIMAP_ATLAS_PLAN.md.
   */
  minimapAtlas: boolean;
  /**
   * Cross-document reference polylines on the minimap. Max+.
   */
  minimapAtlasRefs: boolean;
  /**
   * Heatmap (density / flow) and collaborative-cursor overlays on
   * the minimap. Max+.
   */
  minimapAdvancedOverlays: boolean;
  /**
   * Process Engine — read-only consumption of a Flow-weaver snapshot
   * combined with an Atlas service catalog to build executable
   * attestflows / provisioning / offboarding pipelines. Pro+ to
   * view & design, Max+ to run live, Enterprise for scheduled
   * triggers. `canRunProcesses` (above) still gates the actual
   * `execute()` adapter calls server-side.
   */
  processEngineEnabled: boolean;
  /**
   * Per-entity Details/Bilagor — the "Detaljer" markdown editor +
   * file attachments per block / group / connector. Requires the
   * `entity_details` table + `entity_attachments` storage bucket
   * which are NOT included in the free plan's quota. Advanced+ on
   * paid plans. False = double-click on a block opens an upsell
   * card instead of the editor; FileText / Paperclip indicators on
   * cards are hidden.
   */
  entityDetailsEnabled: boolean;
  /** Can run a process flow live (real side effects). Max+. */
  processEngineExecute: boolean;
  /** AI binding-suggester + risk-classifier. Max+. */
  processEngineAI: boolean;
  /** Daily quota for live process runs per user. */
  processEngineMaxRunsPerDay: number;
  /**
   * /final — read-only executive presentation aggregating Flow-weaver
   * + Atlas + Process Engine. Pro+ (same audience as Atlas).
   */
  finalEnabled: boolean;
  /** AI narrative composer for /final. Max+. */
  finalAI: boolean;
  /**
   * 3D View mobile gestures (touch orbit/pan/pinch + long-press menu).
   * Shipped to Pro+ (#64: the `?mobile3d=1` QA-flag was removed once
   * QA approved; this tier-capability is the sole gate).
   */
  threeDMobile: boolean;
  /**
   * Cloud-sync of block-position overlay deltas via the
   * `sync-three-d-overlay` edge function + `block_overlays` table.
   * Max+. localStorage stays primary; cloud is the mirror.
   */
  threeDCloudOverlay: boolean;
}


export const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: {
    name: 'Free',
    canvasMultiplier: 1,
    canvasWidth: 1920,
    canvasHeight: 1080,
    maxWorkspaces: 1,
    maxCloudStorage: 0,
    maxNestedLayers: 0,
    maxTopLevelGroups: 2,
    maxCoopWorkspaces: 0,
    maxBlocks: 20,
    maxConnectors: 25,
    canExportOverview: false,
    canUseAI: false,
    canSimulate: false,
    canUseAdvancedOverview: false,
    canUseMinimapNav: false,
    canLinkWorkspaces: false,
    maxSheets: 1,
    maxConversations: 10,
    maxArtifactsPerConversation: 3,
    allowedModels: ['google/gemini-3-flash-preview'],
    canRunProcesses: false,
    zoomMode: 'static',
    zoomMin: 0.75,
    zoomMax: 1.0,
    atlasEnabled: false,
    atlasGithubScanner: false,
    atlasAI: false,
    atlasPrivateRepos: false,
    atlasMaxDocs: 0,
    atlasMaxSheetsPerDoc: 0,
    atlasMegaCanvas: false,
    atlasOfflinePinning: false,
    searchCanvasEnabled: false,
    searchProbingEnabled: false,
    codeProbeEnabled: false,
    codeProbeAi: false,
    codeProbeMaxPerDay: 0,
    minimapAtlas: false,
    minimapAtlasRefs: false,
    minimapAdvancedOverlays: false,
    processEngineEnabled: false,
    processEngineExecute: false,
    processEngineAI: false,
    processEngineMaxRunsPerDay: 0,
    entityDetailsEnabled: false,
    finalEnabled: false,
    finalAI: false,
    threeDMobile: false,
    threeDCloudOverlay: false,
  },

  advanced: {
    name: 'Advanced',
    canvasMultiplier: 2,
    canvasWidth: 3840,
    canvasHeight: 2160,
    maxWorkspaces: 5,
    maxCloudStorage: 5,
    maxNestedLayers: 2,
    maxTopLevelGroups: 5,
    maxCoopWorkspaces: 2,
    maxBlocks: 80,
    maxConnectors: 100,
    canExportOverview: true,
    canUseAI: true,
    canSimulate: true,
    canUseAdvancedOverview: true,
    canUseMinimapNav: true,
    canLinkWorkspaces: false,
    maxSheets: 4,
    maxConversations: 15,
    maxArtifactsPerConversation: 10,
    allowedModels: [
      'google/gemini-3-flash-preview',
      'anthropic/claude-haiku-4-5',
    ],
    canRunProcesses: false,
    zoomMode: 'static',
    zoomMin: 0.5,
    zoomMax: 1.0,
    atlasEnabled: false,
    atlasGithubScanner: false,
    atlasAI: false,
    atlasPrivateRepos: false,
    atlasMaxDocs: 0,
    atlasMaxSheetsPerDoc: 0,
    atlasMegaCanvas: false,
    atlasOfflinePinning: false,
    searchCanvasEnabled: false,
    searchProbingEnabled: false,
    codeProbeEnabled: false,
    codeProbeAi: false,
    codeProbeMaxPerDay: 0,
    minimapAtlas: false,
    minimapAtlasRefs: false,
    minimapAdvancedOverlays: false,
    processEngineEnabled: false,
    processEngineExecute: false,
    processEngineAI: false,
    processEngineMaxRunsPerDay: 0,
    entityDetailsEnabled: true,
    finalEnabled: false,
    finalAI: false,
    threeDMobile: false,
    threeDCloudOverlay: false,
  },

  pro: {
    name: 'Pro',
    canvasMultiplier: 4,
    canvasWidth: 7680,
    canvasHeight: 4320,
    maxWorkspaces: 10,
    maxCloudStorage: 10,
    maxNestedLayers: 5,
    maxTopLevelGroups: 10,
    maxCoopWorkspaces: 5,
    maxBlocks: 200,
    maxConnectors: 300,
    canExportOverview: true,
    canUseAI: true,
    canSimulate: true,
    canUseAdvancedOverview: true,
    canUseMinimapNav: true,
    canLinkWorkspaces: true,
    maxSheets: 6,
    maxConversations: 25,
    maxArtifactsPerConversation: 50,
    allowedModels: [
      'google/gemini-3-flash-preview',
      'anthropic/claude-haiku-4-5',
      'anthropic/claude-sonnet-4-6',
      'openai/gpt-4o-mini',
    ],
    canRunProcesses: false,
    zoomMode: 'static',
    zoomMin: 0.7,
    zoomMax: 1.0,
    atlasEnabled: true,
    atlasGithubScanner: false,
    atlasAI: false,
    atlasPrivateRepos: false,
    atlasMaxDocs: 10,
    atlasMaxSheetsPerDoc: 100,
    atlasMegaCanvas: false,
    atlasOfflinePinning: false,
    searchCanvasEnabled: true,
    searchProbingEnabled: false,
    codeProbeEnabled: true,
    codeProbeAi: false,
    codeProbeMaxPerDay: 10,
    minimapAtlas: true,
    minimapAtlasRefs: false,
    minimapAdvancedOverlays: false,
    processEngineEnabled: true,
    processEngineExecute: false,
    processEngineAI: false,
    processEngineMaxRunsPerDay: 0,
    entityDetailsEnabled: true,
    finalEnabled: true,
    finalAI: false,
    threeDMobile: true,
    threeDCloudOverlay: false,
  },

  max: {
    name: 'Max',
    canvasMultiplier: -1,
    canvasWidth: -1,
    canvasHeight: -1,
    maxWorkspaces: 20,
    maxCloudStorage: 20,
    maxNestedLayers: 10,
    maxTopLevelGroups: 15,
    maxCoopWorkspaces: 10,
    maxBlocks: -1,
    maxConnectors: -1,
    canExportOverview: true,
    canUseAI: true,
    canSimulate: true,
    canUseAdvancedOverview: true,
    canUseMinimapNav: true,
    canLinkWorkspaces: true,
    maxSheets: 10,
    maxConversations: 50,
    maxArtifactsPerConversation: 200,
    allowedModels: [
      'google/gemini-3-flash-preview',
      'anthropic/claude-haiku-4-5',
      'anthropic/claude-sonnet-4-6',
      'openai/gpt-4o-mini',
      'anthropic/claude-opus-4-7',
      'openai/gpt-4o',
    ],
    canRunProcesses: false,
    zoomMode: 'dynamic',
    zoomMin: 0.5,
    zoomMax: 1.0,
    atlasEnabled: true,
    atlasGithubScanner: true,
    atlasAI: true,
    atlasPrivateRepos: false,
    atlasMaxDocs: 50,
    atlasMaxSheetsPerDoc: 500,
    atlasMegaCanvas: true,
    atlasOfflinePinning: false,
    searchCanvasEnabled: true,
    searchProbingEnabled: true,
    codeProbeEnabled: true,
    codeProbeAi: true,
    codeProbeMaxPerDay: 100,
    minimapAtlas: true,
    minimapAtlasRefs: true,
    minimapAdvancedOverlays: true,
    processEngineEnabled: true,
    processEngineExecute: true,
    processEngineAI: true,
    processEngineMaxRunsPerDay: 100,
    entityDetailsEnabled: true,
    finalEnabled: true,
    finalAI: true,
    threeDMobile: true,
    threeDCloudOverlay: true,
  },

  enterprise: {
    // Default enterprise limits — overridden by tenant config
    name: 'Enterprise',
    canvasMultiplier: -1,
    canvasWidth: -1,
    canvasHeight: -1,
    maxWorkspaces: -1,
    maxCloudStorage: -1,
    maxNestedLayers: -1,
    maxTopLevelGroups: -1,
    maxCoopWorkspaces: -1,
    maxBlocks: -1,
    maxConnectors: -1,
    canExportOverview: true,
    canUseAI: true,
    canSimulate: true,
    canUseAdvancedOverview: true,
    canUseMinimapNav: true,
    canLinkWorkspaces: true,
    maxSheets: -1,
    maxConversations: -1,
    maxArtifactsPerConversation: -1,
    allowedModels: [
      'google/gemini-3-flash-preview',
      'anthropic/claude-haiku-4-5',
      'anthropic/claude-sonnet-4-6',
      'openai/gpt-4o-mini',
      'anthropic/claude-opus-4-7',
      'openai/gpt-4o',
      // Enterprise kan registrera egna modeller via app_settings
    ],
    // P-001: Enterprise är ENDA tier som får köra workflows som process.
    // Klient-gate via `limits.canRunProcesses`; server-side edge-fn gör
    // dubbel-check mot profiles.plan_tier = 'enterprise'.
    canRunProcesses: true,
    zoomMode: 'dynamic',
    zoomMin: 0.25,
    zoomMax: 1.0,
    atlasEnabled: true,
    atlasGithubScanner: true,
    atlasAI: true,
    atlasPrivateRepos: true,
    atlasMaxDocs: -1,
    atlasMaxSheetsPerDoc: -1,
    atlasMegaCanvas: true,
    atlasOfflinePinning: true,
    searchCanvasEnabled: true,
    searchProbingEnabled: true,
    codeProbeEnabled: true,
    codeProbeAi: true,
    codeProbeMaxPerDay: 1000,
    minimapAtlas: true,
    minimapAtlasRefs: true,
    minimapAdvancedOverlays: true,
    processEngineEnabled: true,
    processEngineExecute: true,
    processEngineAI: true,
    processEngineMaxRunsPerDay: 1000,
    entityDetailsEnabled: true,
    finalEnabled: true,
    finalAI: true,
    threeDMobile: true,
    threeDCloudOverlay: true,
  },

};

/** Returns true if value is unlimited (-1) or within limit */
export function withinLimit(current: number, limit: number): boolean {
  return limit < 0 || current < limit;
}

// ─── Feature gating ─────────────────────────────────────────────────
//
// Centralised plan-gate API: instead of `if (limits.canUseAI)` sprinkled
// across callsites, callers use `canUse(limits, 'ai')` to get a typed
// result with the minimum tier required. UI can then render a graceful
// "Upgrade to Advanced to enable AI" message instead of a blank "nope".

export type Feature =
  | 'ai'
  | 'simulate'
  | 'overview-export'
  | 'advanced-overview'
  | 'minimap-nav'
  | 'link-workspaces'
  | 'coop'
  | 'cloud-storage'
  | 'multi-sheet'
  | 'atlas'
  | 'atlas-github-scanner'
  | 'atlas-ai'
  | 'atlas-private-repos'
  | 'atlas-mega-canvas'
  | 'atlas-offline-pinning'
  | 'search-canvas'
  | 'search-probing'
  | 'minimap-atlas'
  | 'minimap-atlas-refs'
  | 'minimap-advanced-overlays'
  | 'process-engine'
  | 'process-engine-execute'
  | 'process-engine-ai'
  | 'entity-details'
  | 'final'
  | 'final-ai'
  | 'threed-mobile'
  | 'threed-cloud-overlay';


export interface FeatureGateResult {
  ok: boolean;
  /**
   * Lowest tier that unlocks the feature, when `ok === false`. Absent
   * for features that don't get unlocked by a simple tier bump (e.g. a
   * tenant-level toggle). `null` means "enterprise-only / contact sales".
   */
  requiredTier?: PlanTier | null;
}

const FEATURE_MIN_TIER: Record<Feature, PlanTier> = {
  'ai': 'advanced',
  'simulate': 'advanced',
  'overview-export': 'advanced',
  'advanced-overview': 'advanced',
  'minimap-nav': 'advanced',
  'cloud-storage': 'advanced',
  'coop': 'advanced',
  'multi-sheet': 'advanced',
  'link-workspaces': 'pro',
  'atlas': 'pro',
  'atlas-github-scanner': 'max',
  'atlas-ai': 'max',
  'atlas-private-repos': 'enterprise',
  'atlas-mega-canvas': 'max',
  'atlas-offline-pinning': 'enterprise',
  'search-canvas': 'pro',
  'search-probing': 'max',
  'minimap-atlas': 'pro',
  'minimap-atlas-refs': 'max',
  'minimap-advanced-overlays': 'max',
  'process-engine': 'pro',
  'process-engine-execute': 'max',
  'process-engine-ai': 'max',
  'entity-details': 'advanced',
  'final': 'pro',
  'final-ai': 'max',
  'threed-mobile': 'pro',
  'threed-cloud-overlay': 'max',
};


/**
 * Returns `{ ok }` when the given limits grant the feature, or
 * `{ ok: false, requiredTier }` with the lowest tier that would.
 * Works with tenant-overridden enterprise limits too — the boolean
 * flag on `PlanLimits` is the source of truth; `requiredTier` is a
 * hint for UI copy.
 */
export function canUse(limits: PlanLimits, feature: Feature): FeatureGateResult {
  switch (feature) {
    case 'ai':
      return limits.canUseAI ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER.ai };
    case 'simulate':
      return limits.canSimulate ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER.simulate };
    case 'overview-export':
      return limits.canExportOverview ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER['overview-export'] };
    case 'advanced-overview':
      return limits.canUseAdvancedOverview ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER['advanced-overview'] };
    case 'minimap-nav':
      return limits.canUseMinimapNav ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER['minimap-nav'] };
    case 'link-workspaces':
      return limits.canLinkWorkspaces ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER['link-workspaces'] };
    case 'coop':
      return limits.maxCoopWorkspaces !== 0 ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER.coop };
    case 'cloud-storage':
      return limits.maxCloudStorage !== 0 ? { ok: true } : { ok: false, requiredTier: FEATURE_MIN_TIER['cloud-storage'] };
    case 'multi-sheet':
      return limits.maxSheets > 1 || limits.maxSheets < 0
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['multi-sheet'] };
    case 'atlas':
      return limits.atlasEnabled
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER.atlas };
    case 'atlas-github-scanner':
      return limits.atlasGithubScanner
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['atlas-github-scanner'] };
    case 'atlas-ai':
      return limits.atlasAI
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['atlas-ai'] };
    case 'atlas-private-repos':
      return limits.atlasPrivateRepos
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['atlas-private-repos'] };
    case 'atlas-mega-canvas':
      return limits.atlasMegaCanvas
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['atlas-mega-canvas'] };
    case 'atlas-offline-pinning':
      return limits.atlasOfflinePinning
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['atlas-offline-pinning'] };
    case 'search-canvas':
      return limits.searchCanvasEnabled
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['search-canvas'] };
    case 'search-probing':
      return limits.searchProbingEnabled
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['search-probing'] };
    case 'minimap-atlas':
      return limits.minimapAtlas
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['minimap-atlas'] };
    case 'minimap-atlas-refs':
      return limits.minimapAtlasRefs
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['minimap-atlas-refs'] };
    case 'minimap-advanced-overlays':
      return limits.minimapAdvancedOverlays
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['minimap-advanced-overlays'] };
    case 'process-engine':
      return limits.processEngineEnabled
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['process-engine'] };
    case 'process-engine-execute':
      return limits.processEngineExecute
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['process-engine-execute'] };
    case 'process-engine-ai':
      return limits.processEngineAI
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['process-engine-ai'] };
    case 'entity-details':
      return limits.entityDetailsEnabled
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['entity-details'] };
    case 'final':
      return limits.finalEnabled
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER.final };
    case 'final-ai':
      return limits.finalAI
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['final-ai'] };
    case 'threed-mobile':
      return limits.threeDMobile
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['threed-mobile'] };
    case 'threed-cloud-overlay':
      return limits.threeDCloudOverlay
        ? { ok: true }
        : { ok: false, requiredTier: FEATURE_MIN_TIER['threed-cloud-overlay'] };
  }
}


/**
 * User-facing label for a tier (used in graceful-degrade messages).
 */
export function tierDisplayName(tier: PlanTier): string {
  return PLAN_LIMITS[tier]?.name ?? tier;
}

// ─── Enterprise Tenant ──────────────────────────────────────────────
//
// Each enterprise customer is a "tenant" — an isolated container with:
//   - Custom plan limits (overrides from contract)
//   - Their own user pool
//   - Branding/settings
//   - Separate analytics
//
// Tenant configs are stored as JSON in the workflows table
// (category='enterprise-tenant') and managed via the admin dashboard.

export interface EnterpriseTenant {
  id: string;
  /** Company/organization name */
  name: string;
  /** Contact email for the tenant admin */
  adminEmail: string;
  /** Custom plan limits — overrides the default enterprise limits */
  limits: Partial<PlanLimits>;
  /** Max users allowed in this tenant */
  maxUsers: number;
  /** User IDs belonging to this tenant */
  memberIds: string[];
  /** Optional custom branding */
  branding?: {
    primaryColor?: string;
    logoUrl?: string;
    companyName?: string;
  };
  /** Contract metadata */
  contract?: {
    startDate: string;
    endDate?: string;
    notes?: string;
  };
  /** Whether the tenant is active */
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Merge enterprise default limits with tenant-specific overrides */
export function resolveEnterpriseLimits(tenant: EnterpriseTenant): PlanLimits {
  return {
    ...PLAN_LIMITS.enterprise,
    ...tenant.limits,
    name: `Enterprise — ${tenant.name}`,
  };
}

/** Create a new empty tenant config */
export function createDefaultTenant(name: string, adminEmail: string): Omit<EnterpriseTenant, 'id'> {
  return {
    name,
    adminEmail,
    limits: {
      maxWorkspaces: 50,
      maxCloudStorage: 50,
      maxCoopWorkspaces: 20,
      maxBlocks: -1,
      maxConnectors: -1,
      maxNestedLayers: -1,
      maxTopLevelGroups: -1,
    },
    maxUsers: 25,
    memberIds: [],
    branding: {},
    contract: {
      startDate: new Date().toISOString().split('T')[0],
    },
    active: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}
